<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import { ChevronDown, Copy, Pause, Pencil, Play, Trash2 } from 'lucide-vue-next'
import QrcodeVue from 'qrcode.vue'

import CopyLink from '@/components/CopyLink.vue'
import ContestsModal from './Components/ContestsModal.vue'
import ContestStatus from './Components/ContestStatus.vue'

import useSessionStorage from '@/composables/sessionStorage'
import { patchContentScope, useContentScopeSave } from '@/composables/contentScope'
import type { TabKey } from '@/components/base/BaseTab.vue'
import BaseTab from '@/components/base/BaseTab.vue'
import type { ContestGame } from '@/views/contests/ContestsPage.vue'
import ContestsPreview from './Components/ContestsPreview.vue'

import type { Contest as ContestType } from '@/types/types'
import type { ContentScope } from '@/types/contentScope'
import { roundToDecimals } from './utils.js'

interface Store {
  id: number
  slug: string
  name: string
}

interface ShortLink {
  shortLink: string
  clicksCount: number
}

interface StoreWithShortLink {
  storeId: number
  name: string
  shortLink: ShortLink
}

interface ContestGameReward {
  id?: number
  name: string
  probability: number
  color: string
  textColor: string
  rewardTypeSlug: string
  notificationTextOverride: string
  points: number
}

interface Contest {
  id: number
  name: string
  slug: string
  scope?: ContentScope | null
  fromDate: string
  toDate: string
  stores: StoreWithShortLink[]
  shortLink: ShortLink
  contestGameRewards: ContestGameReward[]
  countWonRewards: number
  countRetrievedRewards: number
  isPaused: boolean
  isLegacy?: boolean
  losingProbability: number
  losingTileColor: string
  losingTileTextColor: string
  losingTileText: string
  canLose: boolean
  contestGameBoostType: {
    id: number
    slug: string
  }
}

interface CustomerMerchant {
  id: number
  phone: string
  firstname: string
  gender: string
  hasOptin: boolean
  country: {
    flag: string
  }
}

interface Participant {
  id: number
  customerMerchant: CustomerMerchant
  participatedAt: string
  retrievedAt: string | null
  contestGameReward: ContestGameReward | null
  store: {
    name: string
  }
}

type RewardStatistics = any[]

interface ModalConfig {
  isOpen: boolean
  title: string
  message: string
  confirm: string
  cancel: string
  icon: any
  iconColor?: string
}

const { downloadQrCode } = useDOM()
const { duplicateContestItem } = useSessionStorage()
const { formatNumber } = useNumber()
const { permissions, stores } = useLocalStorage()
const { selectedStore } = useStore()
const { getStoreById } = useStore()
const { t } = useI18n()
const notification = useNotification()
const route = useRoute()
const router = useRouter()

const loaded = ref(false)
const isModalOpen = ref(false)
const isContestTipsModalOpen = ref(false)
const contest = ref<Contest>({} as Contest)
const participants = ref<Participant[]>([])
const rewards = ref<RewardStatistics>([])
const clickTotalCount = ref('')
const qrcodeDownloadable = ref<HTMLElement | null>(null)
const isProcessingDelete = ref(false)

const currentTab = ref((route.query?.activeTab as string) || 'preview')
const tabs = ref<TabKey[]>([
  {
    value: 'preview',
    name: t('pages.marketingFlow.marketingFlowEditPage.preview')
  },
  {
    value: 'performance',
    name: t('pages.marketingFlow.marketingFlowEditPage.performance')
  }
])

const participantsTablePagination = ref({
  currentPage: 1,
  lastPage: 1,
  total: 0
})

const participantsTableColumns = ref({
  customer: {
    label: t('pages.contests.contestDetailPage.tables.thead.customer')
  },
  participatedAt: {
    label: t('pages.contests.contestDetailPage.tables.thead.participatedAt')
  },
  reward: {
    label: t('pages.contests.contestDetailPage.tables.thead.reward')
  },
  retrieved: {
    label: t('pages.contests.contestDetailPage.tables.thead.retrieved')
  },
  store: {
    label: t('pages.contests.contestDetailPage.tables.thead.store')
  }
})

const participantsTableRows = ref([])

const actionsTitle = t('pages.contests.contestDetailPage.buttons.title')

const duplicateModal = ref<ModalConfig>({
  isOpen: false,
  title: t('pages.contests.contestDetailPage.modal.duplicate.title'),
  message: t('pages.contests.contestDetailPage.modal.duplicate.message'),
  confirm: t('pages.contests.contestDetailPage.modal.duplicate.confirm'),
  cancel: t('pages.contests.contestDetailPage.modal.duplicate.cancel'),
  icon: 'HeroSquare2StackIconSolid'
})

const deleteModal = ref<ModalConfig>({
  isOpen: false,
  title: t('pages.contests.contestDetailPage.modal.delete.title'),
  message: t('pages.contests.contestDetailPage.modal.delete.message'),
  confirm: t('pages.contests.contestDetailPage.modal.delete.confirm'),
  cancel: t('pages.contests.contestDetailPage.modal.delete.cancel'),
  icon: 'HeroTrashIconSolid',
  iconColor: 'red'
})

const shareTabs = ref<TabKey[]>([
  {
    value: 'link',
    name: t('pages.contests.contestDetailPage.information.link'),
    icon: 'HeroLinkIconOutline'
  },
  {
    value: 'qrCode',
    name: t('pages.contests.contestDetailPage.information.qrCode'),
    icon: 'HeroQrCodeIconOutline'
  }
])

const currentShareTab = ref<'link' | 'qrCode'>('link')

const boosts = {
  none: {
    icon: 'HeroUserGroupIconSolid',
    title: t('pages.autopilot.autopilotsPage.boost.none')
  },
  facebook: {
    icon: 'IconoirFacebook',
    color: 'icon-facebook',
    title: t('pages.autopilot.autopilotsPage.boost.facebook')
  },
  instagram: {
    icon: 'IconoirInstagram',
    color: 'icon-instagram',
    title: t('pages.autopilot.autopilotsPage.boost.instagram')
  },
  google: {
    icon: 'IconoirGoogleCircle',
    color: 'icon-google',
    title: t('pages.autopilot.autopilotsPage.boost.google')
  },
  custom: {
    icon: 'HeroArrowTopRightOnSquareIconSolid',
    title: t('pages.autopilot.autopilotsPage.boost.custom')
  }
}
const boostSlug = computed(() => {
  return boosts[contest.value?.contestGameBoostType?.slug]
})

const actionsValues = computed(() => {
  const canCreate = permissions.value['contest-game.create']
  const canEdit = permissions.value['contest-game.update']
  const canDelete = permissions.value['contest-game.delete']
  const actions = []

  if (canEdit) {
    actions.push({
      id: 1,
      text: t('pages.contests.contestDetailPage.buttons.edit'),
      icon: Pencil
    })
  }

  if (canCreate) {
    actions.push({
      id: 2,
      text: t('pages.contests.contestDetailPage.buttons.duplicate'),
      icon: Copy
    })
  }

  actions.push({
    id: 4,
    text: contest.value.isPaused
      ? t('pages.contests.contestDetailPage.buttons.unpause')
      : t('pages.contests.contestDetailPage.buttons.pause'),
    icon: contest.value.isPaused ? Play : Pause
  })

  if (canDelete) {
    actions.push({
      id: 3,
      text: t('pages.contests.contestDetailPage.buttons.delete'),
      icon: Trash2,
      variant: 'destructive'
    })
  }
  return actions
})

const qrcodeLink = computed(() => {
  let shortLink = shortLinks.value?.shortLink
  if (shortLink && !/^https?:\/\//i.test(shortLink)) {
    shortLink = `https://${shortLink}`
  }
  return shortLink
})

const shortLinks = computed(() => {
  if (!contest.value.stores || contest.value.stores.length === 0) return null

  if (contest.value.stores.length === 1) {
    return contest.value.stores[0]?.shortLink
  }

  return selectedStore.value === 0
    ? contest.value.shortLink
    : contest.value.stores.filter((store) => store.storeId === selectedStore.value)[0]?.shortLink
})

const availableFilterStores = computed(() => {
  return stores.value?.filter((availableStore: Store) =>
    contest.value.stores.map((store) => store.storeId).includes(availableStore.id)
  )
})

const noBoost = computed(() => {
  return { id: 'none', title: '', description: '' }
})

const showContestExportButton = () => {
  return permissions.value['contest-game.export']
}

const handleStoreSelected = async () => {
  await fetchData()
}

const selectActions = async (event: number) => {
  if (event === 1) {
    router.push({
      name: 'ContestEdit',
      params: { contestId: route.params.contestId }
    })
    return
  }
  if (event === 2) {
    duplicateModal.value.isOpen = true
    return
  }
  if (event === 3) {
    deleteModal.value.isOpen = true
    return
  }
  if (event === 4) {
    await pauseContest()
    return
  }
}

const pauseContest = async () => {
  await axios.patch(`/contest-games/${route.params.contestId}/is-paused`, {
    isPaused: !contest.value.isPaused
  })

  notification.create('success', {
    text: contest.value.isPaused
      ? t('pages.contests.contestDetailPage.unpauseSuccess')
      : t('pages.contests.contestDetailPage.pauseSuccess')
  })

  await fetchContest()
}

const saveScope = useContentScopeSave()

const handleUpdateScope = async (scope: ContentScope) => {
  contest.value.scope = await saveScope(() => patchContentScope('contest-games', String(route.params.contestId), scope))
}

const duplicateContest = () => {
  const params = { ...contest.value }
  params.name = `${params.name} ${t('pages.contests.contestDetailPage.copy')}`
  params.slug = `${params.slug}-${t('pages.contests.contestDetailPage.copy').toLowerCase()}`

  duplicateContestItem.value = params

  router.push({
    name: 'ContestCreate'
  })
}

const deleteContest = () => {
  if (isProcessingDelete.value) {
    return
  }
  isProcessingDelete.value = true
  axios
    .delete(`/contest-games/${route.params.contestId}`)
    .then(() => {
      notification.create('success', {
        text: t('pages.contests.contestDetailPage.modal.notifications.delete.success')
      })
      router.push({
        name: 'ContestList'
      })
    })
    .finally(() => {
      isProcessingDelete.value = false
    })
}

const changePage = (page: number) => {
  participantsTablePagination.value.currentPage = page
  fetchContestParticipants()
}

const fetchContest = async () => {
  try {
    const response = await axios.get(`/contest-games/${route.params.contestId}`, {
      params: {
        storeIds: selectedStore.value
      }
    })

    contest.value = response?.data?.data

    if (contest.value.stores && contest.value.stores.length > 0) {
      const reducer = (accumulator: number, curr: number) => accumulator + curr
      const result = contest.value.stores.map((store) => store.shortLink.clicksCount)
      clickTotalCount.value = result.reduce(reducer).toString()
    }
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      router.push({ name: 'ContestList' })
    }
    if (error.response && error.response.status === 403) {
      notification.create('error', {
        text: t('pages.contests.contestDetailPage.notifications.unauthorized')
      })
      router.push({ name: 'ContestList' })
    }
  }
}

const fetchContestReward = async () => {
  const response = await axios.get(`/contest-games/${route.params.contestId}/reward-statistics`, {
    params: {
      storeIds: selectedStore.value
    }
  })
  rewards.value = response.data.data
}

const fetchContestParticipants = async () => {
  const response = await axios.get(`contest-games/${route.params.contestId}/participants`, {
    params: {
      page: participantsTablePagination.value.currentPage,
      storeIds: selectedStore.value
    }
  })

  participants.value = response.data.data
  const rows = []

  participants.value.forEach((row: Participant) => {
    let retrieved = null
    if (row.contestGameReward && row.contestGameReward.rewardTypeSlug !== 'points') {
      retrieved =
        row.retrievedAt !== null
          ? {
              icon: 'HeroCheckCircleIconSolid',
              color: 'text-green-400'
            }
          : {
              icon: 'HeroXCircleIconSolid',
              color: 'text-red-400'
            }
    }

    rows.push({
      id: row.id,
      data: {
        customer: {
          id: row.customerMerchant.id,
          phone: row.customerMerchant.phone,
          name: row.customerMerchant.firstname,
          gender: row.customerMerchant.gender,
          hasOptin: row.customerMerchant.hasOptin,
          countryFlag: row.customerMerchant.country.flag
        },
        participatedAt: {
          date: new Date(row.participatedAt).toLocaleDateString('fr-FR'),
          time: new Date(row.participatedAt).toLocaleTimeString('fr-FR')
        },
        reward: row.contestGameReward ? row.contestGameReward.name : '-',
        retrieved: retrieved,
        store: row.store.name
      }
    })
  })

  participantsTableRows.value = rows
  participantsTablePagination.value = {
    currentPage: response.data.meta.current_page,
    lastPage: response.data.meta.last_page,
    total: response.data.meta.total
  }
}

const generateLosingProbability = () => {
  contest.value.losingProbability = 100
  contest.value.contestGameRewards.forEach((reward) => {
    contest.value.losingProbability -= reward.probability
  })

  contest.value.losingProbability = roundToDecimals(contest.value.losingProbability)

  contest.value.canLose = contest.value.losingProbability > 0
  if (!contest.value.canLose) {
    contest.value.losingProbability = 25
  }

  if (contest.value.canLose && !contest.value.contestGameRewards.some((reward) => reward.rewardTypeSlug === 'lost')) {
    contest.value.contestGameRewards?.push({
      name: contest.value.losingTileText,
      probability: contest.value.losingProbability,
      color: contest.value.losingTileColor,
      textColor: contest.value.losingTileTextColor,
      notificationTextOverride: '',
      points: 0,
      rewardTypeSlug: 'lost'
    })
  }
}

const fetchData = async () => {
  await Promise.all([fetchContest(), fetchContestParticipants(), fetchContestReward()])
  generateLosingProbability()
}

const openContestTipsModal = () => {
  const hasRouteParams = route.params?.tips && route.params.tips.length > 0
  if (hasRouteParams) {
    isContestTipsModalOpen.value = true
  }
}

const displayToDate = () => {
  return dayjs(contest.value.toDate).format('DD/MM/YYYY') !== '01/01/3000'
}

const formatDate = (date: string) => {
  return dayjs(date).format('D MMMM YYYY')
}

const changeShareTab = (selectedTab: string) => {
  currentShareTab.value = selectedTab as 'link' | 'qrCode'
}

const downloadQr = (element: HTMLElement | null) => {
  if (!element) return

  const isSingleStore = selectedStore.value !== 0
  const name = `${contest.value.slug}${isSingleStore ? `-${getStoreById(selectedStore.value).slug}` : ''}`
  const filename = `pongo-contest-${name}.png`
  downloadQrCode(element, filename)
}

onMounted(async () => {
  selectedStore.value = 0
  loaded.value = false
  await fetchData()
  loaded.value = true
  openContestTipsModal()
})
</script>

<template>
  <div>
    <div class="bg-white">
      <TheDetailHeader
        v-if="contest?.name"
        :title="contest.name"
        :parent="{ label: t('pages.contests.contestsPage.title'), to: { name: 'ContestList' } }"
      >
        <template #badge>
          <ContentScopeDetailBadge :scope="contest.scope" subject="contest_game" :save="handleUpdateScope" />
        </template>
        <template #actions>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="outline" class="w-full md:w-[190px]">
                {{ actionsTitle }}
                <ChevronDown class="ml-auto" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-[190px]">
              <DropdownMenuItem
                v-for="action in actionsValues"
                :key="action.id"
                :variant="action.variant"
                @click="selectActions(action.id)"
              >
                <component :is="action.icon" />
                {{ action.text }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </template>
      </TheDetailHeader>

      <BaseTab v-model="currentTab" :tabs="tabs" is-full-width />
    </div>

    <div v-if="loaded" class="px-6 mb-6 mt-6 flex items-center md:mt-8 sm:mb-8 sm:px-12">
      <div class="flex-1 flex flex-row items-center">
        <BaseIcon name="IconoirFilterList" class="h-6 w-6 text-foreground" />

        <div class="item-center flex-row px-3 text-foreground">
          <span>{{ t('global.filters.filter') }}</span>
        </div>
        <DropdownStore :stores-list="availableFilterStores" @update-dropdown="handleStoreSelected" />
      </div>
    </div>

    <div v-if="loaded" class="w-full">
      <div v-if="currentTab === 'preview'" class="px-6 pb-6 sm:px-12 sm:pb-12 flex flex-row gap-5">
        <div class="flex-1 flex flex-col gap-5">
          <Card class="w-full">
            <CardHeader>
              <CardTitle>{{ t('pages.contests.contestDetailPage.blocks.details.title') }}</CardTitle>
            </CardHeader>

            <CardContent class="flex flex-col gap-4">
              <div class="flex flex-row gap-4">
                <div class="flex-1">
                  <div class="text-xs font-medium text-muted-foreground sm:text-sm">
                    {{ t('pages.contests.contestDetailPage.information.status') }}
                  </div>
                  <ContestStatus class="mt-1" :contest="contest as unknown as ContestGame" />
                </div>

                <div class="flex-1">
                  <div class="text-xs font-medium text-muted-foreground sm:text-sm">
                    {{ t('pages.contests.contestDetailPage.information.stores') }}
                  </div>
                  <StoreTooltip
                    :stores="availableFilterStores"
                    :available-store-ids="availableFilterStores.map((store) => store.id)"
                    position="bottom-start"
                    detail-expandable
                  />
                </div>
              </div>

              <div>
                <div class="text-xs font-medium text-muted-foreground sm:text-sm">
                  {{ t('pages.contests.contestsCreatePage.global.form.fields.boost.label') }}
                </div>
                <div v-if="boostSlug" class="mt-1 flex items-center gap-2">
                  <BaseIcon v-if="boostSlug.icon" :name="boostSlug.icon" :class="boostSlug.color" class="h-5 w-5" />
                  <img v-if="boostSlug.img" :src="boostSlug.img" class="h-5 w-5" />
                  <span class="text-sm text-foreground">{{ boostSlug.title }}</span>
                </div>
              </div>

              <div class="flex flex-row gap-4">
                <div class="flex-1">
                  <div class="text-xs font-medium text-muted-foreground sm:text-sm">
                    {{ t('pages.contests.contestDetailPage.information.fromDate') }}
                  </div>
                  <div class="mt-1 text-xs text-foreground sm:text-sm">
                    {{ formatDate(contest.fromDate) }}
                  </div>
                </div>
                <div v-if="displayToDate()" class="flex-1">
                  <div class="text-xs font-medium text-muted-foreground sm:text-sm">
                    {{ t('pages.contests.contestDetailPage.information.toDate') }}
                  </div>
                  <div class="mt-1 text-xs text-foreground sm:text-sm">
                    {{ formatDate(contest.toDate) }}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{{ t('pages.loyaltySupports.webapp.appearance.share') }}</CardTitle>
            </CardHeader>
            <CardContent class="flex flex-col gap-5">
              <BaseTab
                v-model="currentShareTab"
                :tabs="shareTabs"
                disable-router
                @update:model-value="changeShareTab"
              />
              <div class="flex flex-col gap-2">
                <template v-if="currentShareTab === 'link'">
                  <CopyLink id="shareLink" :link="shortLinks?.shortLink" />
                </template>
                <template v-if="currentShareTab === 'qrCode'">
                  <qrcode-vue id="qrCode" :value="qrcodeLink" :size="96" level="H" render-as="canvas" />
                  <div ref="qrcodeDownloadable" class="hidden">
                    <qrcode-vue :value="qrcodeLink" :size="1000" level="H" />
                  </div>
                  <Button variant="outline" class="max-w-max" @click="downloadQr(qrcodeDownloadable)">
                    {{ t('pages.contests.contestDetailPage.information.downloadQrCode') }}
                  </Button>
                </template>
              </div>
            </CardContent>
          </Card>
        </div>

        <ContestsPreview
          :key="(contest as unknown as ContestType).logo"
          :contest="contest as unknown as ContestType"
          :mechanic="(contest as unknown as ContestType).mechanic || 'fortuneWheel'"
          :selected-boost="noBoost"
        />
      </div>

      <div v-else-if="currentTab === 'performance'" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <AppStatsCard
            :title="t('pages.contests.contestDetailPage.cards.clicks')"
            :content="selectedStore == 0 ? formatNumber(clickTotalCount) : formatNumber(shortLinks?.clicksCount)"
          />

          <AppStatsCard :title="t('pages.contests.contestDetailPage.cards.participation')">
            <div class="flex w-full items-end justify-between">
              <p>{{ formatNumber(participantsTablePagination.total) }}</p>
            </div>
          </AppStatsCard>

          <AppStatsCard
            class="text-left"
            clickable
            :title="t('pages.contests.contestDetailPage.cards.rewardsWon')"
            @click="isModalOpen = !isModalOpen"
          >
            <div class="flex w-full items-end justify-between">
              <p>{{ formatNumber(contest.countWonRewards) }}</p>
              <p class="text-xs font-medium underline">
                {{ t('pages.contests.contestDetailPage.blocks.details.detail') }}
              </p>
            </div>
          </AppStatsCard>
          <AppStatsCard
            clickable
            :title="t('pages.contests.contestDetailPage.cards.rewardsRetrieved')"
            @click="isModalOpen = !isModalOpen"
          >
            <div class="flex w-full items-end justify-between">
              <p>{{ contest.countRetrievedRewards }}</p>
              <p class="text-xs font-medium underline">
                {{ t('pages.contests.contestDetailPage.blocks.details.detail') }}
              </p>
            </div>
          </AppStatsCard>
          <BaseModal
            :is-open="isModalOpen"
            :heading-title="t('pages.contests.contestDetailPage.modal.title')"
            size="3xl"
            :link-text="t('global.words.close')"
            @close="isModalOpen = false"
            @click="isModalOpen = false"
          >
            <ContestsModal :rewards="rewards" />
          </BaseModal>
        </div>

        <div v-if="participantsTableRows && participantsTableRows.length > 0" class="mt-5 overflow-x-auto">
          <AppTable
            :columns="participantsTableColumns"
            :rows="participantsTableRows"
            :pagination="participantsTablePagination"
            @update:change-page="changePage"
          >
            <template #title>
              <div class="flex items-center justify-between bg-white p-4">
                <h3 class="font-semibold text-foreground">
                  {{ t('pages.contests.contestDetailPage.blocks.customerConverted.title') }}
                </h3>
                <div v-if="showContestExportButton()" class="flex space-x-3">
                  <AppDownloadCsvButton
                    v-if="showContestExportButton()"
                    :path="`contest-games/${route.params.contestId}/downloadCsv`"
                    :is-batch="true"
                    class="hidden sm:flex"
                  />
                </div>
              </div>
            </template>

            <template #td-customer="{ data }">
              <RowCustomer
                :id="data.customer.id"
                :phone="data.customer.phone"
                :name="data.customer.name"
                :has-optin="data.customer.hasOptin"
                :country-flag="data.customer.countryFlag"
              />
            </template>

            <template #td-participatedAt="{ data }">
              <div>
                <div class="text-sm font-medium text-foreground">{{ data.participatedAt.date }}</div>
                <div v-if="data.participatedAt.time" class="text-sm text-muted-foreground">
                  {{ data.participatedAt.time }}
                </div>
              </div>
            </template>

            <template #td-reward="{ data }">
              <span>{{ data.reward }}</span>
            </template>

            <template #td-retrieved="{ data }">
              <BaseIcon v-if="data.retrieved" :name="data.retrieved.icon" :class="['h-6 w-6', data.retrieved.color]" />
            </template>

            <template #td-store="{ data }">
              <span>{{ data.store }}</span>
            </template>
          </AppTable>
        </div>

        <Card v-else class="mt-5">
          <CardContent class="flex items-center justify-center py-10">
            <div class="text-center">
              <img
                src="/images/avatars/woman01.jpg"
                class="m-6 inline-block h-28 w-28 rounded-full border-8 border-primary/15"
              />
              <h3 class="text-lg font-semibold text-foreground">
                {{ t('pages.contests.contestDetailPage.blocks.customerConverted.noCustomer.title') }}
              </h3>
              <p class="text-sm text-foreground">
                {{ t('pages.contests.contestDetailPage.blocks.customerConverted.noCustomer.description') }}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    <TheLoadingPage v-else :has-header="false" class="w-full" />
  </div>

  <BaseModal
    :is-open="duplicateModal.isOpen"
    :icon="duplicateModal.icon"
    :link-text="duplicateModal.confirm"
    :text-title="duplicateModal.title"
    :cancel="duplicateModal.cancel"
    :text="duplicateModal.message"
    @close="duplicateModal.isOpen = false"
    @click="duplicateContest"
  />
  <BaseModal
    :is-open="deleteModal.isOpen"
    :icon="deleteModal.icon"
    :icon-color="deleteModal.iconColor"
    :link-text="deleteModal.confirm"
    :text-title="deleteModal.title"
    :cancel="deleteModal.cancel"
    :text="deleteModal.message"
    @close="deleteModal.isOpen = false"
    @click="deleteContest"
  />
</template>
